import React, { useState } from 'react';
import { FiMenu, FiArrowLeft } from 'react-icons/fi';
import {
  HeaderSpacer,
  HeaderContainer,
  HamburgerContainer,
  RightActions,
  IconButton,
} from './styles';
import UrgentMessageModal from '../../chat/sidebar/urgent-message-modal/urgent-message-modal';
import ScheduleMeetingModal from '../../chat/sidebar/schedule-meeting-modal/schedule-meeting-modal';
import Sidebar from '../../chat/sidebar/sidebar';

interface HeaderProps {
  onNotificationClick?: () => void;
  onProfileClick?: () => void;
  notificationCount?: number;
  onCreateJournal?: () => void;

  /** Called when the back arrow is clicked. */
  onBackNavigate?: () => void;

  /** Force the back arrow to show (overrides hamburger). */
  showBackButton?: boolean;

  /** If we're on Settings → Change Password, also force the back button. */
  isChangePasswordTab?: boolean;

  children?: React.ReactNode;
}

const FakeBell = ({ count = 0 }: { count?: number }) => (
  <div style={{ position: 'relative', display: 'inline-block' }}>
    <img
      src="/notification-bell.svg"
      alt="Notifications"
      width={20}
      height={24}
      style={{ display: 'block' }}
    />
    {count > 0 && (
      <span
        style={{
          position: 'absolute',
          top: 12,
          right: -4,
          width: 11,
          height: 11,
          borderRadius: 999,
          background: '#5E84ED',
        }}
      />
    )}
  </div>
);

export const Header: React.FC<HeaderProps> = ({
  onNotificationClick,
  onProfileClick,
  notificationCount = 0,
  onCreateJournal,
  onBackNavigate,
  showBackButton = false,
  isChangePasswordTab = false,
  children,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [urgentOpen, setUrgentOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const handleMenuClick = () => setDrawerOpen(true);
  const handleDrawerClose = () => setDrawerOpen(false);

  const handleCreateJournal = () => {
    if (onCreateJournal) onCreateJournal();
    setDrawerOpen(false);
  };

  const handleBackClick = () => {
    if (onBackNavigate) onBackNavigate();
  };

  // Root-level openers so child components can request these modals
  const handleOpenUrgent = () => {
    // Open urgent first; underlying drawers can close without affecting it
    setUrgentOpen(true);
  };

  // Used by Cancel in schedule modal to fully close everything
  const closeAll = () => {
    setScheduleOpen(false);
    setUrgentOpen(false);
    setDrawerOpen(false);
  };

  const shouldShowBack = showBackButton || isChangePasswordTab;

  return (
    <>
      <HeaderSpacer>
        <HeaderContainer>
          <HamburgerContainer>
            {shouldShowBack ? (
              <FiArrowLeft
                style={{ color: '#B7BDCB', cursor: 'pointer' }}
                size={24}
                onClick={handleBackClick}
                aria-label="Go back"
              />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <FiMenu
                  style={{ color: '#B7BDCB', cursor: 'pointer' }}
                  size={24}
                  onClick={handleMenuClick}
                  aria-label="Open menu"
                />
              </div>
            )}
          </HamburgerContainer>

          <>{children}</>

          <RightActions>
            <IconButton aria-label="Notifications" onClick={onNotificationClick}>
              <FakeBell count={notificationCount} />
            </IconButton>
            <IconButton aria-label="Profile" onClick={onProfileClick}>
              <img
                src="thomas.jpg"
                width={29}
                height={29}
                style={{ borderRadius: '19px', marginBottom: '4px' }}
              />
            </IconButton>
          </RightActions>
        </HeaderContainer>
      </HeaderSpacer>

      {!shouldShowBack && (
        <Sidebar
          open={drawerOpen}
          onClose={handleDrawerClose}
          onCreateJournal={handleCreateJournal}
          // Allow Drawer/TalkToChris to open root-level modals
          onOpenUrgent={handleOpenUrgent}
        />
      )}

      {/* Urgent message (root-mounted) */}
      <UrgentMessageModal
        open={urgentOpen}
        onOpenChange={setUrgentOpen}
        onSend={(_message: string) => {
          setUrgentOpen(false);
        }}
      />

      {/* Schedule a Meeting (fullscreen below header) */}
      <ScheduleMeetingModal
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        onCancelAll={closeAll}
        headerHeight={81}
        // windows={yourApiWindows} // optionally pass availability ISO windows here
      />
    </>
  );
};
